/* The small pieces every page is built from.
 *
 * Deliberately plain: these carry the blueprint look and nothing else, so a
 * page reads as layout plus data rather than layout plus styling. */

import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Link } from 'react-router-dom'

// --------------------------------------------------------------- panels
export function Blueprint({ children, style, className = '' }: {
  children: ReactNode
  style?: CSSProperties
  className?: string
}) {
  return (
    <div className={`blueprint ${className}`} style={{ padding: '20px 22px 18px', ...style }}>
      <i className="corner tl" /><i className="corner tr" />
      <i className="corner bl" /><i className="corner br" />
      {children}
    </div>
  )
}

export function PanelHead({ title, hint, right }: {
  title: string
  hint?: ReactNode
  right?: ReactNode
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap',
    }}>
      <h2 style={{ fontSize: 22, margin: 0 }}>{title}</h2>
      {right ?? (hint ? <div style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>{hint}</div> : null)}
    </div>
  )
}

/** A panel that folds away.
 *
 * Deliberately not <details>: printing must show the contents whether or not
 * the reader happened to leave it open, and a native <details> stays shut in
 * the print stylesheet. Open state is React state, and the print rules in
 * ds.css force `.collapsed-body` visible. */
export function Collapsible({ title, hint, defaultOpen = false, right, children }: {
  title: string
  hint?: ReactNode
  defaultOpen?: boolean
  right?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Blueprint className="sheet">
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            background: 'transparent', border: 0, padding: 0, cursor: 'pointer',
            font: 'inherit', color: 'var(--color-text)', textAlign: 'left',
          }}
        >
          <span aria-hidden="true" style={{
            display: 'inline-block', fontSize: 11, color: 'var(--color-accent-700)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms ease',
          }}>▶</span>
          <h2 style={{ fontSize: 22, margin: 0 }}>{title}</h2>
        </button>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          {hint ? <span style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>{hint}</span> : null}
          {right}
          <button type="button" className="btn btn-ghost no-print"
            style={{ fontSize: 12 }} onClick={() => setOpen(!open)}>
            {open ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>
      <div className="collapsed-body" hidden={!open}>{children}</div>
    </Blueprint>
  )
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="pretty" style={{
      fontSize: 13, color: 'var(--color-neutral-700)', margin: '4px 0 14px', maxWidth: 780,
    }}>{children}</div>
  )
}

/** Where a page's upload control would be, for an editor: files are handed
 *  in on the Data loads page and reach this page once an admin accepts
 *  them. An admin keeps the control itself. */
export function LoadElsewhere() {
  return (
    <span className="no-print" style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
      Files are loaded from the <Link to="/loads">Data loads</Link> page; an admin accepts
      them before they appear here.
    </span>
  )
}

// ------------------------------------------------------------------ KPIs
export interface Kpi {
  label: string
  value: string
  unit?: string
  note?: string
  tone?: 'good' | 'bad' | 'plain'
}

export function KpiRow({ items, min = 190 }: { items: Kpi[]; min?: number }) {
  return (
    <section className="sheet" style={{
      display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 16,
    }}>
      {items.map((k, i) => (
        <Blueprint key={i} style={{ padding: '14px 16px 16px' }}>
          <div className="eyebrow">{k.label}</div>
          <div className="kpi-value" style={{
            color: k.tone === 'bad' ? 'var(--color-bad)'
              : k.tone === 'good' ? 'var(--color-good)' : 'inherit',
          }}>
            {k.value}
            {k.unit ? (
              <span style={{ fontSize: 16, color: 'var(--color-neutral-600)', marginLeft: 4 }}>{k.unit}</span>
            ) : null}
          </div>
          {k.note ? <div className="kpi-note">{k.note}</div> : null}
        </Blueprint>
      ))}
    </section>
  )
}

// -------------------------------------------------------------- controls
export function Segmented<T extends string | number>({ value, options, onChange, minWidth }: {
  value: T
  options: { value: T; label: ReactNode; count?: ReactNode }[]
  onChange: (v: T) => void
  minWidth?: number
}) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className={`btn ${o.value === value ? 'btn-primary' : 'btn-secondary'}`}
          style={minWidth ? { minWidth } : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count !== undefined ? (
            <span style={{ opacity: 0.6, marginLeft: 6, fontSize: 11 }}>{o.count}</span>
          ) : null}
        </button>
      ))}
    </div>
  )
}

export function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span className="axis-title">{label}</span>
      {children}
    </div>
  )
}

export function Slider({ label, value, min, max, step, display, note, onChange }: {
  label: string
  value: number
  min: number
  max: number
  step: number
  display: string
  note?: string
  onChange: (v: number) => void
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <label className="field-label">{label}</label>
        <span className="field-value">{display}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {note ? <div className="field-note">{note}</div> : null}
    </div>
  )
}

/** Strip a leading zero the moment a real digit follows it.
 *
 *  "03" -> "3", but "0", "0.5" and "-0.5" are left alone. */
const stripLeadingZero = (s: string): string => s.replace(/^(-?)0+(?=\d)/, '$1')

/** A number input that does not strand a leading zero.
 *
 *  React refuses to rewrite a controlled number input's DOM value when the
 *  text already parses to the same number it is being given — so a box
 *  showing 0 that you type 3 into keeps reading "03" on screen for good,
 *  even though state moved to 3 on the first keystroke. Holding the
 *  in-progress text here and normalising it as it is typed means the box
 *  always shows what was typed, minus the stray zero. The draft is dropped
 *  on blur so the parent's own clamping or rounding takes the field back.
 *
 *  `onEmpty` is for the fields where a cleared box means "not recorded"
 *  rather than zero; without it, clearing simply holds the last value until
 *  something parseable is typed. */
export function NumberField({ value, onChange, onEmpty, ...rest }: {
  value: number | null
  onChange: (v: number) => void
  onEmpty?: () => void
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <input
      {...rest}
      type="number"
      value={draft ?? (value == null ? '' : String(value))}
      onChange={(e) => {
        const text = stripLeadingZero(e.target.value)
        setDraft(text)
        // "-" and "." are keystrokes on the way to a number, not numbers.
        if (text === '' || text === '-' || text === '.' || text === '-.') {
          onEmpty?.()
          return
        }
        const n = Number(text)
        if (Number.isFinite(n)) onChange(n)
      }}
      onBlur={(e) => {
        setDraft(null)
        rest.onBlur?.(e)
      }}
    />
  )
}

// ----------------------------------------------------------- page states
export function Loading({ what }: { what: string }) {
  return <div style={{ padding: '48px 0', color: 'var(--color-neutral-600)' }}>Loading {what}…</div>
}

export function ErrorBox({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <Blueprint style={{ borderColor: 'var(--color-bad)' }}>
      <div className="eyebrow" style={{ color: 'var(--color-bad)' }}>Something went wrong</div>
      <div style={{ margin: '8px 0 12px', fontSize: 14 }}>{error}</div>
      {onRetry ? <button className="btn btn-secondary" onClick={onRetry}>Try again</button> : null}
    </Blueprint>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Blueprint>
      <h2 style={{ fontSize: 22, margin: 0 }}>{title}</h2>
      <Note>{children}</Note>
    </Blueprint>
  )
}

// ---------------------------------------------------------------- legend
/** `outline` draws the swatch as a hollow box, matching series that are
 *  themselves drawn as outlines (target bars) rather than filled. */
export function Legend({ items }: {
  items: { color: string; label: ReactNode; line?: boolean; outline?: boolean }[]
}) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '10px 22px', marginTop: 12,
      fontSize: 12, color: 'var(--color-neutral-700)',
    }}>
      {items.map((it, i) => (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{
            width: it.line ? 16 : 12,
            height: it.line ? 2 : 10,
            background: it.outline ? 'transparent' : it.color,
            border: it.outline ? `1px solid ${it.color}` : undefined,
            display: 'inline-block',
            flex: 'none',
          }} />
          {it.label}
        </span>
      ))}
    </div>
  )
}

/** A horizontal bar in a track — the shape used for pareto, mix and stock. */
export function BarRow({ label, width, value, secondWidth, colors = ['var(--c-bar3)', 'var(--c-bar4)'], tip }: {
  label: ReactNode
  width: number
  secondWidth?: number
  value: ReactNode
  colors?: [string, string] | string[]
  /** Hover readout. Row bars already show a label and a total, so this is
   *  for the breakdown behind them — the per-unit split of a stoppage, a
   *  share of the month — which the row itself has no room for. */
  tip?: ReactNode
}) {
  const [over, setOver] = useState(false)
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: '150px 1fr auto',
        alignItems: 'center', gap: 12, position: 'relative',
      }}
      onMouseEnter={tip ? () => setOver(true) : undefined}
      onMouseLeave={tip ? () => setOver(false) : undefined}
    >
      <div className="truncate" style={{ fontSize: 13, color: 'var(--color-neutral-800)' }}>{label}</div>
      <svg viewBox="0 0 300 14" preserveAspectRatio="none" style={{ width: '100%', height: 14, display: 'block' }}>
        <rect x="0" y="0" width="300" height="14" fill="var(--c-track)" />
        <rect x="0" y="0" width={Math.max(0, width)} height="14" fill={colors[0]} />
        {secondWidth ? (
          <rect x={Math.max(0, width)} y="0" width={Math.max(0, secondWidth)} height="14" fill={colors[1]} />
        ) : null}
      </svg>
      <div className="num" style={{ fontSize: 13, textAlign: 'right', minWidth: 96, color: 'var(--color-neutral-800)' }}>
        {value}
      </div>
      {tip && over ? (
        <div style={{
          position: 'absolute', left: '50%', bottom: '100%', transform: 'translate(-50%, -8px)',
          width: 190, pointerEvents: 'none', zIndex: 5,
          background: 'var(--color-bg)', border: '1px solid var(--color-divider)',
          boxShadow: 'var(--shadow-md)', padding: '8px 10px', fontSize: 12, lineHeight: 1.45,
        }}>{tip}</div>
      ) : null}
    </div>
  )
}
